<template>
  <div class="crafting-produce">
    <CraftingDescriptionPopup
      v-if="$device.isMobile && isModalOpen"
      :title="$t('craftingRecipeLimitTitle')"
      @toggle="handleToggle"
    >
      <CraftingQueueLimitContent />
    </CraftingDescriptionPopup>
    <div ref="craftingProduceContentRef" class="crafting-produce-content">
      <div v-if="recipeSpeedTranslation" class="crafting-produce-name">
        <span>
          {{ $t(tokenMetadata?.label, { count: 0 }) }}
        </span>
        <UIChevron :type="ChevronType.Warning" :text="recipeSpeedTranslation" />
      </div>
      <div v-else class="crafting-produce-name">
        {{ produceName }}
      </div>

      <div class="crafting-info" @click="handleToggle">
        <div class="crafting-recipe-limit-label">
          <span style="color: rgba(255, 255, 255, 0.5)">{{ $t('craftingRecipeLimit') }}:</span>
        </div>
        <div class="crafting-recipe-limit">
          <div class="crafting-recipe-total-limit">
            <span>{{ $t('craftingPersonalLimit') }} — </span>
            <span>&nbsp;</span>
            <div class="crafting-recipes-value">
              <el-skeleton :loading="isLoading || recipeRequestStatus === 'pending'" animated style="max-height: 17px">
                <template #template>
                  <el-skeleton-item style="width: 30px; height: 18px" />
                </template>

                <template #default>
                  <span
                    class="crafting-default-color"
                    :class="{
                      'crafting-danger-color': notEnoughRecipesForCrafting
                    }"
                  >
                    {{ maxAmountAvailableForCraft }}
                  </span>
                  <span
                    v-if="currentRecipe?.queueType === 0 && BigNumber(currentRecipe?.duration || 0).isGreaterThan(0)"
                    >,&nbsp;</span
                  >
                </template>
              </el-skeleton>
            </div>
          </div>

          <div
            v-if="currentRecipe?.queueType === 0 && BigNumber(currentRecipe?.duration || 0).isGreaterThan(0)"
            class="crafting-queue-limit"
          >
            <span>{{ $t('craftingTotalLimit') }} —</span>
            <span>&nbsp;</span>
            <div class="crafting-queue-limit-value">
              <el-skeleton
                :loading="isLoading || recipeRequestStatus === 'pending' || extraDelay"
                animated
                style="max-height: 17px"
              >
                <template #template>
                  <el-skeleton-item style="width: 40px; height: 18px" />
                </template>

                <template #default>
                  <div
                    class="crafting-default-color"
                    :class="{
                      'crafting-danger-color': maxAvailableRecipesInMaxQueue === 0 || !currentRecipe?.isActive,
                      'crafting-warning-color': maxAvailableRecipesInMaxQueue < 10 && maxAvailableRecipesInMaxQueue > 0,
                      blink: isAnimatedBlink && queueLimitFilled
                    }"
                  >
                    <span>{{ maxAvailableRecipesInMaxQueue }}</span>
                  </div>
                </template>
              </el-skeleton>
            </div>
            <span class="divider-vertical">|</span>

            <el-tooltip
              trigger="hover"
              popper-class="popper-default"
              :disabled="$device.isMobile"
              placement="right-start"
            >
              <template #content>
                <CraftingQueueLimitContent />
              </template>
              <InfoIcon class="info-icon" />
            </el-tooltip>
          </div>
        </div>
        <el-tooltip
          v-if="BigNumber(currentRecipe?.duration || 0).isZero()"
          :disabled="$device.isMobile"
          placement="bottom-end"
          popper-class="popper-default"
        >
          <template #content>
            <CraftingModalInfoTooltip />
          </template>
          <InfoIcon class="info-icon" />
        </el-tooltip>
      </div>

      <div class="crafting-tabs">
        <div
          class="crafting-tabs-tab"
          :class="[
            selectedTab === CraftingItemTab.RECIPE && currentRecipe?.isActive && 'active',
            !currentRecipe?.isActive && 'disabled'
          ]"
          @click="() => handleClickTab(CraftingItemTab.RECIPE)"
        >
          <span>{{ $t('craftingTabName1') }}</span>
          <span class="countdown">(<CraftingRecipeTimeCountDown :time="craftingRecipeTime" />) </span>
        </div>
        <div
          v-if="BigNumber(currentRecipe?.duration || 0).isGreaterThan(0)"
          class="crafting-tabs-tab"
          :class="selectedTab === CraftingItemTab.CRAFTING && 'active'"
          @click="() => handleClickTab(CraftingItemTab.CRAFTING)"
        >
          <span>{{ $t('craftingTabName2') }}</span>
          <span class="countdown">(<CraftingQueueTimeCountDown :time="queueTimeInitial" />) </span>
        </div>
      </div>
      <CraftingRecipe
        v-if="selectedTab === CraftingItemTab.RECIPE && currentRecipe"
        :recipe-id="currentRecipe.recipeId"
        :nft-name="produceName"
      />
      <CraftingRunningRecipes
        v-if="selectedTab === CraftingItemTab.CRAFTING && currentRecipe"
        :token-metadata="props.tokenMetadata"
        :recipe-id="currentRecipe?.recipeId"
        :is-claim-button-loading="props.isClaimButtonLoading"
        @claim-load-state-changed="(isLoading) => emit('claimLoadStateChanged', isLoading)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { CraftingQueueStatusMessage, QueueTypes, RecipeType, type TTokensConfigItem } from '~/utils/constants';
import { type BuildingProps, CraftingItemTab, type RecipeProps, type RunningRecipeProps } from '~/types/crafting';
import useEnvs from '~/composables/useEnvs';
import CraftingRecipeTimeCountDown from '~/components/Crafting/CraftingRecipeTimeCountDown.vue';
import { BigNumber } from 'bignumber.js';
import CraftingQueueTimeCountDown from '~/components/Crafting/CraftingQueueTimeCountDown.vue';
import InfoIcon from '~/components/icons/InfoIcon.vue';
import CraftingQueueLimitContent from '~/components/Crafting/CraftingQueueLimitContent.vue';
import { ZeroHash } from 'ethers';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { ChevronType } from '~/utils/constants/ui';
import { WebSocketsKeys } from '~/types/common';

const { t } = useI18n();
const { address } = useWeb3ModalAccount();
const config = useRuntimeConfig();
const { apiUrl } = useEnvs();
const { isMobile } = useDevice();
const route = useRoute();
const store = useMainStore();
const craftingProduceContentRef = ref();
const isAnimatedBlink = ref(false);
const [isModalOpen, toggleModal] = useToggle();
const queueLimitCountDownRef = ref();
const currentRecipe = inject<Ref<RecipeProps>>('currentRecipe');
const { $websockets: websockets } = useNuxtApp();
const craftingSocket = websockets?.[WebSocketsKeys.craftingActivity];

const selectedTab = ref(!currentRecipe?.value.isActive ? CraftingItemTab.CRAFTING : CraftingItemTab.RECIPE);
const now = useTimestamp({ interval: 1000 });

const recipeIdPrefix = computed(() => {
  const env = config?.public.env;
  return env === 'prod' ? 'prodRecipeId' : env === 'demo' ? 'demoRecipeId' : 'testnetRecipeId';
});
const produceName = computed(() => t(`${recipeIdPrefix.value}${currentRecipe?.value?.recipeId}`));

const props = defineProps<{
  tokenMetadata: TTokensConfigItem;
  buildingKey: string;
  parentElementRef: HTMLElement | null;
  isClaimButtonLoading: boolean;
}>();

const emit = defineEmits<{
  claimLoadStateChanged: [isLoading: boolean];
}>();

const toggleModalQueueTimeInjected = inject<() => void>('toggleModalQueueTime');
const building = inject<Ref<BuildingProps>>('building');
const extraDelay = ref(false);

watch(currentRecipe!, () => {
  if (!currentRecipe?.value.isActive) selectedTab.value = CraftingItemTab.CRAFTING;
  if (BigNumber(currentRecipe?.value.duration || 0).isZero()) selectedTab.value = CraftingItemTab.RECIPE;
});

// reset crafting table height
watch(extraDelay, () => {
  if (extraDelay.value && props.parentElementRef) {
    props.parentElementRef.style.setProperty('height', 'auto');
  }
});

const { maxAmountAvailableForCraft, isLoading, isEnoughTokens } = useMaxAmountAvailableForCraft();

const { data: recipes } = useNuxtData<RecipeProps[]>(
  `buildingRecipes${isMobile && route.name !== 'districts' ? building?.value.buildingHash : ''}`
);

const computedRecipeId = computed(() => currentRecipe?.value.recipeId);

const { data: runningRecipes, refresh: refreshRunningRecipes } = useFetch<RunningRecipeProps[]>(
  () => `/crafting/recipes/in-queue`,
  {
    baseURL: apiUrl,
    watch: [
      () => store.notificationUpdateVersion,
      () => building?.value.buildingHash,
      computedRecipeId,
      craftingSocket
    ],
    query: {
      recipeId: computedRecipeId,
      buildingHash: building?.value.buildingHash || ZeroHash
    },
    key: 'recipes/in-queue',
    onRequest: () => {
      queueLimitCountDownRef.value?.abortCountDown();
      queueTime.value = 0;
    },
    transform: (data) => {
      if (currentRecipe && currentRecipe.value.queueType === QueueTypes.COMMON) return data;

      return data.filter((recipe) => recipe.userAddress === address.value);
    }
  }
);

const { data: recipeToCraft, status: recipeRequestStatus } = useFetch<RecipeProps>(
  () => `/crafting/recipes/${currentRecipe?.value.recipeId}`,
  {
    watch: [() => store.notificationUpdateVersion, computedRecipeId],
    baseURL: apiUrl,
    key: 'recipes/recipe',
    onResponse: async () => {
      // Extra delay for ensuring that the queueTime has been recalculated first
      //  to avoid the redundant UI updates (of queueTimeLimit)
      extraDelay.value = true;
      await new Promise((resolve) => setTimeout(resolve, 1000));
      extraDelay.value = false;
    }
  }
);

onMounted(() => {
  refreshRunningRecipes();
});

const currentTime = computed(() => Date.now());

const getRecipeTime = (time: string) => new Date(time).getTime();

const runningRecipe = computed(() => {
  return runningRecipes.value?.find((recipe) => {
    return getRecipeTime(recipe.craftStartTime) <= now.value && now.value <= getRecipeTime(recipe.craftEndTime);
  });
});

const runningCount = computed(() => {
  if (!runningRecipes?.value) return 0;
  return runningRecipes.value.reduce((sum, recipe) => sum + (recipe.multiplyModifier || 1), 0);
});

const maxAvailableRecipesInMaxQueue = computed(() => {
  if (!currentRecipe?.value?.maxQueue || !currentRecipe.value.isActive) return 0;

  const duration = recipeToCraft.value?.duration ?? 0;
  if (duration === 0) return 0;
  const craftedItems = runningRecipe.value?.craftStartTime
    ? Math.floor((currentTime.value - getRecipeTime(runningRecipe.value.craftStartTime)) / (duration * 1000))
    : 0;

  return currentRecipe.value.maxQueue - runningCount.value + craftedItems;
});

const craftingRecipeTime = computed(() => (recipeToCraft.value?.duration || 0) * 1000);

const lastRunningRecipeEndTime = computed(
  () => Math.max(...(runningRecipes.value?.map((rec) => new Date(rec.craftEndTime).getTime()) || [])) || 0
);

const queueTimeInitial = computed(() => {
  if (BigNumber(store.notificationUpdateVersion).isNaN()) return 0;
  return BigNumber(lastRunningRecipeEndTime.value - Date.now()).isPositive()
    ? lastRunningRecipeEndTime.value - Date.now()
    : 0;
});

const queueTime = ref(
  BigNumber(lastRunningRecipeEndTime.value - Date.now()).isPositive() ? lastRunningRecipeEndTime.value - Date.now() : 0
);

const notEnoughRecipesForCrafting = computed(() => maxAmountAvailableForCraft.value === 0);

const queueTimeLimit = computed(() => {
  if (BigNumber(store.notificationUpdateVersion).isNaN()) return 0;
  return BigNumber(recipeToCraft.value?.duration || 0)
    .multipliedBy(1000)
    .multipliedBy(
      recipes.value?.find((recipe) => BigNumber(recipe.recipeId).isEqualTo(recipeToCraft.value?.recipeId || 0))
        ?.maxQueue || 0
    )
    .minus(queueTime.value)
    .toNumber();
});

const { messageType } = useQueueLimitStatus(recipeToCraft, queueTimeLimit, maxAmountAvailableForCraft);

const queueLimitFilled = computed(() => {
  const isInstantRecipe = currentRecipe?.value && currentRecipe.value.duration === 0;

  return (
    !maxAmountAvailableForCraft.value &&
    isEnoughTokens.value &&
    !isLoading.value &&
    !isInstantRecipe &&
    messageType.value &&
    messageType.value !== CraftingQueueStatusMessage.PERSONAL_QUEUE_OVERLOAD
  );
});

const updateQueueTime = () => {
  const isPositive = BigNumber(lastRunningRecipeEndTime.value - Date.now()).isPositive();
  if (!isPositive) {
    queueTime.value = 0;
    return;
  }

  queueTime.value = lastRunningRecipeEndTime.value - Date.now();
};

const setQueueTime = (value: number) => {
  queueTime.value = value;
};

const handleClickTab = (tab: CraftingItemTab) => {
  if (!currentRecipe?.value?.isActive) return;
  selectedTab.value = tab;
};

const recipeSpeedTranslation = computed(() => {
  const recipeNameEN = t(
    `${recipeIdPrefix.value}${currentRecipe?.value?.recipeId}`,
    {},
    {
      locale: 'en'
    }
  );

  if (recipeNameEN.toLocaleLowerCase().includes(RecipeType.SUPERFAST)) {
    return t('craftingSpeedSuperFast');
  }

  if (recipeNameEN.toLocaleLowerCase().includes(RecipeType.FAST)) {
    return t('craftingSpeedFast');
  }

  return '';
});

const handleToggle = () => {
  toggleModalQueueTimeInjected && toggleModalQueueTimeInjected();
  toggleModal();
};

const animateBlink = useDebounceFn(() => {
  isAnimatedBlink.value = true;

  setTimeout(() => {
    isAnimatedBlink.value = !isAnimatedBlink.value;
  }, 3000);
}, 1000);

provide('recipeRequestStatus', recipeRequestStatus);
provide('lastRunningRecipeEndTime', lastRunningRecipeEndTime);
provide('queueTime', queueTime);
provide('queueTimeLimit', queueTimeLimit);
provide('updateQueueTime', updateQueueTime);
provide('setQueueTime', setQueueTime);
provide('animateBlink', animateBlink);
provide('isAnimatedBlink', isAnimatedBlink);
provide('maxAmountAvailableForCraft', maxAmountAvailableForCraft);
provide('maxAvailableRecipesInMaxQueue', maxAvailableRecipesInMaxQueue);
provide('isMaxAmountAvailableForCraftLoading', isLoading);
provide('isEnoughTokens', isEnoughTokens);
provide('getRecipeTime', getRecipeTime);
provide('runningRecipe', runningRecipe);
provide('reactiveNow', now);
</script>

<style lang="scss">
.crafting {
  &-tabs {
    display: flex;
    width: 100%;
    box-shadow: 2px 4px 5px 0px #00000040;
    min-height: 55px;
    user-select: none;

    &-tab {
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      width: 100%;
      background: radial-gradient(59.02% 71.08% at 84.01% 85.82%, #040f14 0%, #081b25 100%);
      font-size: 20px;
      font-family: Grenze;
      line-height: 29.6px;
      text-align: center;
      color: #fff;
      border: 1px solid #1e353e;
      flex-direction: column;

      &.active {
        background: linear-gradient(180deg, #0083ad 0%, #00354f 100%);
      }

      &.disabled {
        opacity: 0.5;
        cursor: default;
      }

      .countdown {
        font-family: Eczar;
        font-weight: 500;
        font-size: 15px;
        line-height: 15px;
        text-align: center;

        color: rgba(255, 255, 255, 0.5);

        span {
          display: inline-flex;
        }
      }
    }
  }

  &-info {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding-top: 4px;
    padding-bottom: 10px;

    font-family: Eczar;
    font-weight: 500;
    font-size: 16px;
    line-height: 17.6px;
    text-align: center;
    color: rgba(255, 255, 255, 1);

    @media screen and (max-width: 900px) {
      gap: 4px;
      flex-direction: column;
    }

    .divider-vertical {
      color: #1e353e;
      display: block;
      margin: 0 8px;
    }

    .crafting-default-color {
      color: #49ddaa;
    }

    .crafting-danger-color {
      color: #f6164c;
    }

    .crafting-warning-color {
      color: #ff6711;
    }

    .crafting-recipe-total-limit {
      display: flex;
    }

    .crafting-recipe-limit {
      display: flex;
      align-items: center;
      justify-content: center;

      @media screen and (max-width: 390px) {
        flex-direction: column;
      }
    }

    .crafting-queue-limit {
      display: flex;

      @media screen and (max-width: 900px) {
        justify-content: center;
      }
    }

    .info-icon {
      cursor: pointer;
      height: 15px;
      width: 15px;
      max-height: 15px;
    }

    &-duration {
      width: 100%;
      display: flex;
      justify-content: center;
    }
  }

  &-info-row {
    border-bottom: 2px solid #ffffff1a;
    padding: 10px;

    display: flex;
    justify-content: space-evenly;
    gap: 10px;

    @media screen and (max-width: 900px) {
      flex-direction: column;
      align-items: center;
    }

    div {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
      gap: 5px;

      .timer-label {
        color: #90989d;
      }

      span {
        font-size: 20px;
        font-weight: 500;
        font-family: Grenze;
        margin-bottom: 0;
        color: #fff;
      }
    }

    &-time {
      font-family: Eczar;
      font-weight: 500;
      font-size: 20px;
      color: #fff;
      padding: 18px 0;

      &-ready {
        color: #49ddaa;
      }
    }
  }

  &-produce {
    background: #0d242f;
    border: 1px solid #1e353e;
    padding-top: 16px;
    padding-bottom: 16px;
    height: 100%;
    border-radius: 15px;

    .crafting-produce-content {
      min-height: 380px;
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    &-name {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      color: #fff;
      font-weight: 700;
      font-family: Grenze;
      font-size: 26px;
      margin-bottom: 0;
      text-transform: capitalize;
      text-align: center;

      span {
        width: min-content;
      }
    }

    &-time {
      margin-bottom: 20px;

      color: #fff;
      font-weight: 400;

      font-size: 20px;
    }

    @media screen and (max-width: 900px) {
      max-height: 100%;

      &-button {
        width: auto;
      }
    }
  }

  &-buttons {
    margin-top: 17px;
    padding: 0 16px;
    display: flex;
    align-items: stretch;
    justify-content: center;
    flex-wrap: wrap;
    gap: 40px;

    @media screen and (max-width: 900px) {
      justify-content: center;
    }

    & > * {
      margin-left: 10px;

      @media screen and (max-width: 900px) {
        margin-left: 0;
        margin-top: 10px;
      }
    }
  }

  .blink {
    display: flex;
    color: var(--error);
    animation:
      blink 3s linear,
      scale 2s ease-in;
  }

  @keyframes blink {
    0%,
    100% {
      color: var(--error);
    }

    50% {
      color: #ff144d;
    }
  }

  @keyframes scale {
    0%,
    100% {
      transform: scale(1);
    }

    50% {
      transform: scale(1.125);
    }
  }
}
</style>
